import { Link, useParams } from 'react-router'
import { IconArrowLeft, Spinner, Text, VisuallyHidden } from '@n26/nxd'
import type { CardStub } from '@/features/dashboard/api/schemas'
import { ROUTES } from '@/shared/routes'
import { formatLike, toFigure } from '@/features/dashboard/format/figure'
import { sentenceCase } from '@/features/dashboard/format/format'
import { isKpi } from '@/features/dashboard/model/sections'
import { useDashboardFilters } from '@/features/dashboard/filters/useDashboardFilters'
import {
  useDashboardCard,
  useDashboardStructure,
} from '@/features/dashboard/api/queries'
import { BarChart } from '@/features/dashboard/charts/BarChart'
import { EmptyCardMessage } from '@/features/dashboard/tiles/EmptyCardMessage'
import { CardFigure } from '@/features/dashboard/tiles/CardFigure'
import { InfoTip } from '@/features/dashboard/tiles/InfoTip'
import styles from './DeepDivePage.module.css'

const Loading = () => (
  <div role='status'>
    <Spinner />
    <VisuallyHidden>Loading</VisuallyHidden>
  </div>
)

type DeepDiveCardProps = {
  stub: CardStub
}

const CardTitle = ({ stub }: DeepDiveCardProps) => (
  <div className={styles.titleRow}>
    <h2 className={styles.cardTitle}>{stub.name}</h2>
    {stub.description && (
      <InfoTip name={stub.name} description={stub.description} />
    )}
  </div>
)

const DeepDiveCard = ({ stub }: DeepDiveCardProps) => {
  const { data: card, isPending, isError } = useDashboardCard(stub.cardId)
  const { clearMarkets } = useDashboardFilters()

  if (isPending) return <Loading />
  if (isError) {
    return (
      <div role='alert'>
        <Text color='rhubarb'>Couldn&apos;t load this card.</Text>
      </div>
    )
  }

  if (card.kind === 'kpi') {
    const figure = toFigure(card)
    return (
      <section className={styles.kpi} aria-label={card.name}>
        <CardTitle stub={stub} />
        {figure && (
          <CardFigure
            figure={figure}
            size='expanded'
            className={styles.kpiValues}
          />
        )}
        {figure && figure.previous !== null && (
          <p className={styles.previous}>
            Previous: {formatLike(figure, figure.previous)}
          </p>
        )}
      </section>
    )
  }

  if (card.kind === 'empty') {
    return (
      <section className={styles.chart} aria-label={card.name}>
        <CardTitle stub={stub} />
        <EmptyCardMessage reason={card.reason} onClear={clearMarkets} />
      </section>
    )
  }

  return (
    <section className={styles.chart} aria-label={card.name}>
      <CardTitle stub={stub} />
      <BarChart card={card} />
    </section>
  )
}

export const DeepDivePage = () => {
  // Deep dives are intentionally unfiltered for now: no market or range applies.
  const { sectionId } = useParams()
  const { data: dashboard, isPending, isError } = useDashboardStructure()

  if (isPending) return <Loading />
  if (isError) {
    return (
      <div role='alert'>
        <Text color='rhubarb'>Couldn&apos;t load the dashboard.</Text>
      </div>
    )
  }

  const section = dashboard.sections.find(
    candidate => candidate.id === sectionId && candidate.kind === 'deep_dive'
  )
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Link
          to={ROUTES.HOME}
          className={styles.back}
          aria-label='Back to dashboard'
        >
          <IconArrowLeft aria-hidden />
        </Link>
        <h1 className={styles.title}>
          {section ? sentenceCase(section.title) : 'Deep dive'}
        </h1>
      </div>
      {section ? (
        <>
          {section.cards.some(isKpi) && (
            <div className={styles.kpis}>
              {section.cards.filter(isKpi).map(stub => (
                <DeepDiveCard key={stub.cardId} stub={stub} />
              ))}
            </div>
          )}
          <div className={styles.charts}>
            {section.cards
              .filter(stub => !isKpi(stub))
              .map(stub => (
                <DeepDiveCard key={stub.cardId} stub={stub} />
              ))}
          </div>
        </>
      ) : (
        <Text>Deep dive not found.</Text>
      )}
    </div>
  )
}
